import { holdsAnywhere } from '@carii/casl-abilities';
import { csv, ringgit } from '../finance/financeCsv.js';

/**
 * 412 (#1505): what this member may do with penalties, platform-wide (the server asks the same actions). `manage`
 * (Admin) answers yes to each; the two money powers are given per role.
 */
export interface PenaltyPowers {
  read: boolean;
  create: boolean;
  update: boolean;
  confirm: boolean;
  payout: boolean;
}
export function penaltyPowers(ability: unknown): PenaltyPowers {
  const a = ability as never;
  return {
    read: holdsAnywhere(a, 'read', 'Penalty'),
    create: holdsAnywhere(a, 'create', 'Penalty'),
    update: holdsAnywhere(a, 'update', 'Penalty'),
    confirm: holdsAnywhere(a, 'confirm-payment', 'Penalty'),
    payout: holdsAnywhere(a, 'payout', 'Penalty'),
  };
}

/** 371 (#1241): the penalty tracker's wire shapes, words and CSV (the sheet's penalty tab, column for column). */
export const PENALTY_TYPES = [
  'late_return',
  'fuel',
  'wrong_car',
  'no_photos',
  'cancellation',
  'car_locked',
  'cleanliness',
  'other',
  // 417 (#1464): a confirmed km charge; recorded from the booking's Km charge card, never by Add penalty.
  'mileage',
] as const;
export type PenaltyType = (typeof PENALTY_TYPES)[number];
export const PENALTY_TYPE_LABEL: Record<PenaltyType, string> = {
  late_return: 'Late return',
  fuel: 'Fuel',
  wrong_car: 'Wrong car',
  no_photos: 'No photos',
  cancellation: 'Cancellation',
  car_locked: 'Car locked',
  cleanliness: 'Cleanliness',
  other: 'Other',
  mileage: 'Extra kilometres',
};
/** RM 10,000, the server's limit. */
export const PENALTY_MAX_MINOR = 1_000_000;

export interface Penalty {
  id: string;
  booking_id: string;
  rental_id: string;
  booking_ref: string;
  rental_ref: string;
  customer: { id: string; name: string; phone: string };
  car: { id: string; plate_number: string; label: string };
  supplier: { id: string; name: string };
  host_membership_id: string | null;
  type: PenaltyType;
  amount_minor: number;
  on: string;
  reason: string;
  status: 'active' | 'cancelled';
  cancel_reason: string;
  customer_paid_on: string | null;
  host_paid_out_on: string | null;
  created_by_name: string;
  /** 393 (#1313): the customer's payment receipt and staff's decision on it. */
  receipt_status?: 'under_review' | 'rejected' | 'approved' | null;
  receipt_uploaded_at?: string | null;
  receipt_reject_reason?: string | null;
  /**
   * 409 (#1471): CARii's share of the fine and the supplier's payout, at the rate kept when it was paid out (`kept`)
   * or the platform's current one. Computed by the platform; the admin never works it out.
   */
  commission: { rate_bps: number; kept: boolean; commission_minor: number; payout_minor: number };
}
export interface PenaltyList {
  items: Penalty[];
  totals: {
    active_count: number;
    cancelled_count: number;
    amount_minor: number;
    unpaid_minor: number;
    /** 409: the suppliers' share of the active penalties not yet paid out. */
    payout_pending_minor: number;
    /** 409: CARii's share of the active penalties. */
    commission_minor: number;
    /** 393: receipts waiting for a decision, whatever the filter. */
    under_review_count?: number;
  };
  truncated: boolean;
}

export const rm = (minor: number) =>
  `RM ${(minor / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** `2026-06-10` → `10 Jun 2026`, the day itself (no time zone involved). */
export function dayWords(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
/** 393: "15 Oct, 21:20" in Malaysia time. */
export const uploadedWords = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

export const mytToday = () => new Date(Date.now() + 8 * 3600_000).toISOString().slice(0, 10);

/** The list's totals line, as the design reads it. */
export function totalsLine(t: PenaltyList['totals']): string {
  const n = `${t.active_count} active ${t.active_count === 1 ? 'penalty' : 'penalties'}`;
  const cancelled = t.cancelled_count > 0 ? ` (${t.cancelled_count} cancelled not counted)` : '';
  return `${n}${cancelled} · ${rm(t.amount_minor)} · Unpaid ${rm(t.unpaid_minor)} · CARii commission ${rm(t.commission_minor)} · Payout pending ${rm(t.payout_pending_minor)}`;
}

/** 409: a rate in basis points as the admin writes it, `25 %` or `12.5 %`. */
export function ratePercent(bps: number): string {
  return `${(bps / 100).toLocaleString('en-MY', { maximumFractionDigits: 2 })} %`;
}

export function penaltiesToCsv(list: PenaltyList): string {
  return csv([
    [
      'Date',
      'Customer',
      'Phone',
      'Supplier',
      'Penalty type',
      'Booking',
      'Amount (RM)',
      'CARii commission (RM)',
      'Payout (RM)',
      'Reason',
      'Vehicle',
      'Recorded by',
      'Paid by customer',
      'Supplier payout',
      'Status',
      'Cancel reason',
    ],
    ...list.items.map((p) => [
      p.on,
      p.customer.name,
      p.customer.phone,
      p.supplier.name,
      PENALTY_TYPE_LABEL[p.type] ?? p.type,
      p.booking_ref,
      ringgit(p.amount_minor),
      ringgit(p.commission.commission_minor),
      ringgit(p.commission.payout_minor),
      p.reason,
      `${p.car.plate_number} ${p.car.label}`.trim(),
      p.created_by_name,
      p.customer_paid_on ?? 'Unpaid',
      p.host_paid_out_on ?? 'Pending',
      p.status === 'cancelled' ? 'Cancelled' : 'Active',
      p.cancel_reason,
    ]),
  ]);
}
